import { getToken, onMessage } from 'firebase/messaging';
import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { messagingPromise } from '../firebase/firebase';

const TOKEN_STORAGE_KEY = 'le-defi:fcm-token-id';
let foregroundListenerInstalled = false;

async function tokenId(token) {
  const bytes = new TextEncoder().encode(token);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

function platformName() {
  const agent = navigator.userAgent.toLowerCase();
  if (agent.includes('android')) return 'android-web';
  if (agent.includes('iphone') || agent.includes('ipad') || agent.includes('ipod')) return 'ios-web';
  return 'web';
}

async function registerWorker() {
  if (!('serviceWorker' in navigator)) {
    throw new Error('Ce navigateur ne prend pas en charge les service workers.');
  }

  const registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
  await navigator.serviceWorker.ready;
  return registration;
}

function installForegroundListener(messaging) {
  if (foregroundListenerInstalled || !messaging) return;
  foregroundListenerInstalled = true;

  onMessage(messaging, (payload) => {
    const title = payload.notification?.title || payload.data?.title || 'Le Défi';
    const body = payload.notification?.body || payload.data?.body || 'Vous avez reçu une nouvelle notification.';

    if (Notification.permission === 'granted') {
      new Notification(title, {
        body,
        icon: '/icons/icon-192.png',
        data: payload.data || {}
      });
    }
  });
}

export async function hasRegisteredNotificationToken({ user, db }) {
  if (!user?.uid) return false;
  const storedId = localStorage.getItem(TOKEN_STORAGE_KEY);
  if (!storedId) return false;

  try {
    const snapshot = await getDoc(doc(db, 'users', user.uid, 'notificationTokens', storedId));
    return snapshot.exists() && snapshot.data()?.enabled === true;
  } catch {
    return false;
  }
}

export async function requestBrowserNotifications({ user, profile, db }) {
  if (!user?.uid) {
    return { permission: 'denied', registered: false, message: 'Vous devez être connecté.' };
  }

  if (!('Notification' in window)) {
    return {
      permission: 'unsupported',
      registered: false,
      message: 'Ce navigateur ne prend pas en charge les notifications.'
    };
  }

  try {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      return {
        permission,
        registered: false,
        message: 'Les notifications n’ont pas été autorisées sur cet appareil.'
      };
    }

    const messaging = await messagingPromise;
    if (!messaging) {
      throw new Error('Les notifications ne sont pas disponibles sur ce navigateur.');
    }

    const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;
    if (!vapidKey) {
      throw new Error('La clé VAPID est absente du fichier .env.');
    }

    const serviceWorkerRegistration = await registerWorker();
    const token = await getToken(messaging, { vapidKey, serviceWorkerRegistration });
    if (!token) {
      throw new Error('Impossible d’activer les notifications sur cet appareil.');
    }

    const id = await tokenId(token);
    await setDoc(doc(db, 'users', user.uid, 'notificationTokens', id), {
      token,
      enabled: true,
      accountType: profile?.accountType || profile?.role || null,
      parentId: profile?.parentId || null,
      platform: platformName(),
      userAgent: navigator.userAgent,
      updatedAt: serverTimestamp(),
      createdAt: serverTimestamp()
    }, { merge: true });

    localStorage.setItem(TOKEN_STORAGE_KEY, id);
    installForegroundListener(messaging);

    return {
      permission,
      registered: true,
      message: 'Notifications activées et appareil enregistré.'
    };
  } catch (error) {
    console.error('Activation des notifications :', error);
    return {
      permission: Notification.permission,
      registered: false,
      message: error?.message || 'Impossible d’activer les notifications sur cet appareil.'
    };
  }
}
